import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from 'reactstrap';
import DataTable from 'react-data-table-component';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import ActionButton from '../../Components/Common/ActionButton';
import StatusBadge from '../../Components/Common/StatusBadge';
import ListPager from '../../Components/Common/ListPager';
import { ListSearch } from '../../Components/Common/ListFilters';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify from '../../utils/notify';
import { pick } from '../../shared/offer-domain/pick';
import '../TenderOpening/components/TenderOpening.css';
import './components/GtcView.css';
import { PUBLISH_TONE, gtcApi, monthName, publishLabel } from './data/gtcApi';
import { GTC_TABLE_STYLES } from './GtcView';

export const AMEND_TONE = { Amended: 'info', 'Not Amended': 'muted' };

// Master Amend GTC (Registered Supplier): published + active GTCs with the
// logged-in Mitra's amendment status.
const GtcAmendView = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [denied, setDenied] = useState(false);
  const [sort, setSort] = useState({ field: null, desc: false });

  document.title = 'Master Amend GTC';

  const loadData = useCallback(async () => {
    const reqId = ++requestRef.current;
    setIsLoading(true);
    try {
      const data = await gtcApi.listAmend({
        PageNumber: currentPage,
        PageSize: rowsPerPage,
        Search: search || undefined,
        SortBy: sort.field || undefined,
        IsDescending: sort.field ? sort.desc : undefined,
      });
      if (reqId !== requestRef.current) return; // stale response
      setDenied(false);
      setRows(pick(data, 'items', 'Items') || []);
      setTotalRows(pick(data, 'totalItems', 'TotalItems') || 0);
    } catch (err) {
      if (reqId !== requestRef.current) return;
      setDenied((err?.response?.status ?? err?.status) === 403);
      notify.apiError(err, 'load GTC');
      setRows([]);
      setTotalRows(0);
    } finally {
      if (reqId === requestRef.current) setIsLoading(false);
    }
  }, [search, currentPage, rowsPerPage, sort]);

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => setSearch(filterText.trim()), 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const columns = [
    {
      id: 'gtcNumber',
      name: 'ID GTC',
      selector: row => row.gtcNumber,
      sortable: true,
      width: '11%',
      cell: row => <span className='gtc-id'>{row.gtcNumber ?? '-'}</span>,
    },
    {
      id: 'title',
      name: 'GTC TITLE',
      selector: row => row.title,
      sortable: true,
      width: '33%',
      cell: row => (
        <div className='gtc-title-cell'>
          <span className='gtc-title-cell__title'>{row.title ?? '-'}</span>
          <span className='gtc-title-cell__desc'>{row.description || '-'}</span>
        </div>
      ),
    },
    {
      id: 'year',
      name: 'YEAR',
      selector: row => row.year,
      sortable: true,
      width: '8%',
      center: true,
      cell: row => <span>{row.year ?? '-'}</span>,
    },
    {
      id: 'month',
      name: 'MONTH',
      selector: row => row.month,
      sortable: true,
      width: '10%',
      center: true,
      cell: row => <span>{monthName(row.month) || '-'}</span>,
    },
    {
      id: 'publishStatus',
      name: 'PUBLISH STATUS',
      width: '12%',
      center: true,
      cell: row => (
        <StatusBadge
          label={publishLabel(row)}
          tone={PUBLISH_TONE[publishLabel(row)]}
        />
      ),
    },
    {
      id: 'amendStatus',
      name: 'AMEND STATUS',
      width: '14%',
      center: true,
      cell: row => {
        const label = row.amendStatus || 'Not Amended';
        return (
          <StatusBadge
            label={
              row.amendedClauseCount
                ? `${label} (${row.amendedClauseCount})`
                : label
            }
            tone={AMEND_TONE[label]}
          />
        );
      },
    },
    {
      name: <span className='text-center w-100'>ACTION</span>,
      width: '12%',
      center: true,
      cell: row => (
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-eye-line'
          onClick={() => navigate(`/apps-gtc-amend/dtl/${row.id}`)}
        >
          View Detail
        </ActionButton>
      ),
    },
  ];

  return (
    <div className='page-content'>
      <Container fluid>
        <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
          <div className='email-content minimal-border'>
            <div className='p-4'>
              <div className='to-hero mb-4'>
                <PageBreadcrumb
                  fallback={{
                    label: 'Master Amend GTC',
                    to: '/apps-gtc-amend/view',
                  }}
                />
                <div className='d-flex align-items-center gap-3 flex-wrap'>
                  <span className='to-hero-icon'>
                    <i className='ri-file-edit-line' aria-hidden='true' />
                  </span>
                  <div className='flex-grow-1'>
                    <h4 className='to-hero-title'>MASTER AMEND GTC</h4>
                    <p className='to-hero-subtitle'>
                      List of published General Terms and Conditions (GTC)
                      documents available for Registered Supplier amendment
                      review.
                    </p>
                  </div>
                  <button
                    type='button'
                    className='to-hero-btn'
                    onClick={loadData}
                  >
                    <i className='ri-refresh-line' aria-hidden='true' />
                    Refresh
                  </button>
                </div>
              </div>

              <div className='to-panel'>
                <div className='to-toolbar'>
                  <ListSearch
                    value={filterText}
                    onChange={e => {
                      setFilterText(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder='Search GTC by id or title...'
                    ariaLabel='Search GTC'
                  />
                </div>
                <div className='to-table-wrap'>
                  <DataTable
                    columns={columns}
                    data={rows}
                    onSort={(column, direction) => {
                      setSort({ field: column.id, desc: direction === 'desc' });
                      setCurrentPage(1);
                    }}
                    sortServer
                    highlightOnHover
                    persistTableHead
                    progressPending={isLoading}
                    progressComponent={<SkeletonRows />}
                    noDataComponent={
                      denied ? (
                        <EmptyState
                          icon='ri-lock-line'
                          title='You do not have access'
                          emptyHint='This account does not have access to Master Amend GTC.'
                        />
                      ) : (
                        <EmptyState
                          title='No GTC found'
                          emptyHint='No Published GTC documents available for Registered Supplier yet.'
                          filteredHint={`No GTC matches "${filterText}".`}
                          hasFilters={Boolean(filterText)}
                          onClear={() => setFilterText('')}
                        />
                      )
                    }
                    customStyles={GTC_TABLE_STYLES}
                  />
                </div>
                <ListPager
                  currentPage={currentPage}
                  rowsPerPage={rowsPerPage}
                  totalRows={totalRows}
                  onPageChange={setCurrentPage}
                  onRowsPerPageChange={size => {
                    setRowsPerPage(size);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default GtcAmendView;
